import ky, {
  type Input,
  type NormalizedOptions,
  type Options,
} from 'ky';
import {
  UserManager,
  type UserManagerSettings,
} from 'oidc-client-ts';

import {
  QueryNotAuthorizedError,
  QueryNotFoundError,
} from '#components/errors/QueryError';
import {
  getOidcConfig,
} from '#features/auth/OidcConfig';
import {
  traceSpan,
} from '#src/helpers/otel/trace';

const requestInitDefaults: Options = {
  headers: {
    'Content-Type': 'application/json',
  },
  credentials: 'same-origin',
  cache: 'default',
  mode: 'cors',
  referrerPolicy: 'origin-when-cross-origin',
};

export async function getUserManager() {
  const oidcConfig = getOidcConfig(window.location.origin);
  return new UserManager(oidcConfig as UserManagerSettings);
}

export async function getUser(userManager?: UserManager) {
  const usrMgr = userManager
    ? userManager
    : await getUserManager();
  return usrMgr.getUser();
}

export async function defaultFetchErrorHandler(
  res: Response,
  req: Request,
  options: NormalizedOptions,
): Promise<Error> {
  const status = res.status;
  try {
    const bodyJson = await res.text();
    console.error(`Error: Fetch response code ${res.status}:\n`, bodyJson);
  } catch (err: unknown) {
    console.error(`Error: Unable to get error response body: `, err, res);
  }
  if (status === 403) {
    throw new QueryNotAuthorizedError(
      res,
      req,
      options,
    );
  } else if (status === 404) {
    throw new QueryNotFoundError(
      res,
      req,
      options,
    );
  } else {
    throw new Error(`Fetch response status code not OK: ${status}`);
  }
}

export type AuthFetchErrorHandler = typeof defaultFetchErrorHandler;

export async function useAuthFetchRaw<T = unknown>(
  req: string | URL | Request | Input,
  init?: Options,
  errorHandler: AuthFetchErrorHandler = defaultFetchErrorHandler,
): Promise<{
    data: T;
    headers: Headers;
    res: Response;
  }> {
  // Merge the RequestInit parameter with application defaults
  const options: Options = {
    ...requestInitDefaults,
    ...init,
  };
  // Get user instance
  const userManager = await getUserManager();
  const user = await getUser(userManager);
  // console.debug(user);
  if (user && options?.headers) { // Add user access token to headers
    options.headers['Authorization'] = `Bearer ${user.access_token}`;
  } else { // Error if user token not available
    console.warn('User session instance not found');
    const response = {
      status: 403,
    };
    throw new QueryNotAuthorizedError(
      response as Response,
      {} as Request,
      {} as NormalizedOptions,
    );
  }
  const res = await ky<T>(
    req,
    options,
  );
  if (res.ok) {
    const data = (await res.json()) as T;
    return {
      data,
      headers: res.headers,
      res,
    };
  } else {
    const err = await errorHandler(
      res,
      req as Request,
      options as NormalizedOptions,
    );
    throw err;
  }
}

export type AuthFetchProps = Parameters<typeof useAuthFetchRaw>;

export async function useAuthFetch<T>(...args: AuthFetchProps) {
  return traceSpan(
    useAuthFetchRaw<T>,
    args,
    'useAuthFetch',
  );
}

export default useAuthFetch;
